<script lang="ts" setup>
	import FilterTooltipComponent from '@/components/filters/FilterTooltipComponent.vue';
	import VarietyFilterOptionComponent from '@/components/filters/VarietyFilterOptionComponent.vue';
	import {useFilters} from '@/store/filter.store';
	import {useProductTableStore} from '@/store/table.store';
	import type {Filter} from '@/types/search/filter';
	import type {Tooltip} from '@/types/search/tooltip';
	import {computed} from 'vue';
	import VarietyFilterActiveOptionsComponent from './VarietyFilterActiveOptionsComponent.vue';

	defineEmits(['filters-updated']);

	const filterStore = useFilters();
	const tableStore = useProductTableStore();

	const props = defineProps<{
		filter?: Filter;
		tooltip?: Tooltip;
	}>();

	const expanded = computed(() => {
		const expandedFilter = filterStore.expandedFilter();
		return expandedFilter ? expandedFilter.name === props.filter?.name : false;
	});

	const collapseColumnAmount = computed(() => props.filter?.configuration?.expandedColumns ?? 1);

	const hasActiveOptions = computed(() => {
		return props.filter?.options.some(option => option.active);
	});

	const renderFirstOptionLabels = computed(() => {
		const firstOptions = props.filter?.options ?? [];

		return firstOptions
			.slice(0, 20)
			.map(option => option.label)
			.join(', ');
	});

	const expandFilter = (): void => {
		if (props.filter) {
			filterStore.setExpandedFilterName(props.filter.name);
		}
	};

	const collapseFilter = (): void => {
		filterStore.resetExpandedFilter();
	};

	const toggleFilter = (event: Event): void => {
		const element = event.target as HTMLElement;

		// If the tooltip was clicked, don't expand the filter options
		if (element.classList.contains('tooltip-icon')) {
			return;
		}

		expanded.value ? collapseFilter() : expandFilter();
	};
</script>

<template>
	<div v-if="filter" :class="[expanded ? 'options-open' : '']" class="filter-part filter-part--options">
		<div class="option-part-header">
			<h3 class="filter-part__title" @click="toggleFilter($event)">
				<span class="filter-part__title__icon">
					<svg class="icon-chevron-right" height="12" width="9">
						<use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#chevron-right"></use>
					</svg>
				</span>

				{{ filter.title }}

				<FilterTooltipComponent :name="filter.name" :tooltip="tooltip" />
			</h3>

			<div v-if="hasActiveOptions" :id="'filter-part__selected-filters_' + filter.name"
			     class="filter-part__selected-filters">
				<template v-for="option in filter.options" :key="option.id">
					<VarietyFilterActiveOptionsComponent
							v-if="option.active"
							:filter-name="filter.name"
							:option="option"
							@filters-updated="$emit('filters-updated')" />
				</template>
			</div>

			<p v-if="!hasActiveOptions" class="filter-part__description" @click="toggleFilter">
				<!-- 20 options should always be enough to render, as it should fill one entire line in the filter block. -->
				{{ renderFirstOptionLabels }}
			</p>

		</div>
		<div :class="'columns-' + collapseColumnAmount" class="filter-part-options">
			<div class="filter-part-options__inner">
				<button class="filter-part-options__close" @click.prevent="collapseFilter">
					<svg class="icon-modal-close" height="20" width="20">
						<use xlink:href="/assets/images/build/svg-sprite.min.svg#modal-close"></use>
					</svg>
				</button>
				<ul class="filter-checklist">
					<VarietyFilterOptionComponent v-for="option in filter.options" :key="option.id"
					                              :disabled="tableStore.loading"
					                              :filter-name="filter.name"
					                              :option="option"
					                              @filters-updated="$emit('filters-updated')" />
				</ul>
			</div>
		</div>
	</div>
</template>
